import { buildColumnTypeMap, cellTypeForColumn } from '../../reporting-core/column-types';
import { ReportCellType } from '../../reporting-core/cell-format';
import { coloredText, escapeHtml, link, renderStandardCell, ReportRenderConfig } from '../../reporting-core/cell-html';
import { dateStringFromISO8601, propExists, timeStringFromISO8601 } from '../../reporting-core/utils';
import { CustomInboxItem } from '../custom-inbox';
import { getContentItem, getCustomInboxItemStatusDisplay } from '../exports/custom-inbox-items';
import {
  COLUMN_NAME_ITEM_ID,
  COLUMN_NAME_TIMESTAMP,
  COLUMN_NAME_INBOX_ID,
  COLUMN_NAME_INBOX_NAME,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_NAME,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_CONDITIONS_MET,
  COLUMN_NAME_RESOLVED_AT,
  COLUMN_NAME_RESOLVED_BY,
  COLUMN_NAME_RESOLVED_BY_ID,
  COLUMN_NAME_RESOLVED_BY_EMAIL,
} from '../exports/custom-inbox-items';

export {
  COLUMN_NAME_ITEM_ID,
  COLUMN_NAME_TIMESTAMP,
  COLUMN_NAME_INBOX_ID,
  COLUMN_NAME_INBOX_NAME,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_NAME,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_CONDITIONS_MET,
  COLUMN_NAME_RESOLVED_AT,
  COLUMN_NAME_RESOLVED_BY,
  COLUMN_NAME_RESOLVED_BY_ID,
  COLUMN_NAME_RESOLVED_BY_EMAIL,
};

// Synthetic display-only column: the customer's name as a link to their
// profile. The exportable name/id/email columns stay in the exports module.
export const COLUMN_NAME_CUSTOMER = 'Customer';

export const COLUMN_HEADER_OVERRIDES: {[key: string]: string} = {
  [COLUMN_NAME_INBOX_NAME]: 'Inbox',
};

export const REPORT_COLUMN_NAMES = [
  COLUMN_NAME_TIMESTAMP,
  COLUMN_NAME_CUSTOMER,
  COLUMN_NAME_INBOX_NAME,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_RESOLVED_AT,
  COLUMN_NAME_RESOLVED_BY,
];

export const DEFAULT_COLUMNS = [
  COLUMN_NAME_TIMESTAMP,
  COLUMN_NAME_CUSTOMER,
  COLUMN_NAME_INBOX_NAME,
];

export const COLUMN_TYPES = buildColumnTypeMap({
  [ReportCellType.custom]: [
    COLUMN_NAME_TIMESTAMP,
    COLUMN_NAME_CUSTOMER,
    COLUMN_NAME_STATUS,
    COLUMN_NAME_RESOLVED_AT,
    COLUMN_NAME_RESOLVED_BY,
  ],
});

/** "3:45 PM, Jun 8, 2026" — the inbox page's timestamp format. */
function timestampText(iso8601: string, config: ReportRenderConfig): string {
  const timezone = config.timezone ?? null;
  return (
    timeStringFromISO8601(iso8601, timezone) + ', ' +
    dateStringFromISO8601(iso8601, timezone)
  );
}

export function getDisplayText(item: CustomInboxItem, column: string,
                               config: ReportRenderConfig): string {
  switch (column) {
    case COLUMN_NAME_TIMESTAMP:
      return `<span class="text-nowrap">${escapeHtml(timestampText(item.created_at, config))}</span>`;
    case COLUMN_NAME_CUSTOMER:
      return link(config, '/customers/' + item.customer_id,
                  escapeHtml(item.customer_first_name) + ' ' + escapeHtml(item.customer_last_name));
    case COLUMN_NAME_STATUS: {
      const cls = item.is_resolved ? 'text-nowrap' : 'text-nowrap text-danger';
      return coloredText(cls, getCustomInboxItemStatusDisplay(item));
    }
    case COLUMN_NAME_RESOLVED_AT:
      return item.resolved_at
        ? `<span class="text-nowrap">${escapeHtml(timestampText(item.resolved_at, config))}</span>`
        : coloredText('text-muted', 'None');
    case COLUMN_NAME_RESOLVED_BY:
      // Items resolved automatically (the inbox's auto-resolve time) have no
      // staff member to name.
      return propExists(item.resolved_by_id)
        ? escapeHtml(getContentItem(item, COLUMN_NAME_RESOLVED_BY))
        : coloredText('text-muted', 'None');
    default:
      return renderStandardCell(getContentItem(item, column), cellTypeForColumn(COLUMN_TYPES, column), config);
  }
}
